import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { EventSourcePolyfill } from 'event-source-polyfill';

const HEARTBEAT_TIMEOUT = 20000; // 20s, server sends ~15s heartbeat
const API_KEY_HEADER_NAME = 'x-api-key';
const API_KEY_VALUE = process.env.REACT_APP_PORTAL_API_KEY;

interface SSEState {
  isConnected: boolean;
  error: Error | null;
  retryCount: number;
  isRefreshingToken: boolean;
  hasReachedMaxRetries: boolean;
  lastHeartbeatTime: number | null;
}

export const useOrderSSE2 = (
  selectedCellId: number,
  landingZoneId?: string,
  enabled: boolean = true
) => {
  const dispatch = useDispatch();

  const eventSourceRef = useRef<EventSource | null>(null);

  const [sseState, setSSEState] = useState<SSEState>({
    isConnected: false,
    error: null,
    retryCount: 0,
    isRefreshingToken: false,
    hasReachedMaxRetries: false,
    lastHeartbeatTime: null
  });

  const closeIfOpen = () => {
    if (eventSourceRef.current) {
      try {
        eventSourceRef.current.close();
      } catch (_) {}
      eventSourceRef.current = null;
    }
  };

  const connect = () => {
    if (!enabled) {
      return;
    }

    closeIfOpen();

    const baseUrl = process.env.REACT_APP_ORDERFUL_URL;
    let sseUrl: string;
    if (landingZoneId) {
      sseUrl = `${baseUrl}/ordercards/sse/${selectedCellId}/landingzone/${landingZoneId}`;
    } else {
      sseUrl = `${baseUrl}/ordercards/sse/${selectedCellId}`;
    }

    const init: any = {
      headers: {
        // API key auth
        [API_KEY_HEADER_NAME]: API_KEY_VALUE,
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
      },
      heartbeatTimeout: HEARTBEAT_TIMEOUT,
      withCredentials: false
    };

    const es = new EventSourcePolyfill(sseUrl, init) as unknown as EventSource;
    eventSourceRef.current = es;

    es.onopen = () => {
      setSSEState(prev => ({
        ...prev,
        isConnected: true,
        error: null,
        retryCount: 0,
        hasReachedMaxRetries: false,
        lastHeartbeatTime: Date.now()
      }));
    };

    es.addEventListener('connected', () => {
      setSSEState(prev => ({
        ...prev,
        isConnected: true,
        lastHeartbeatTime: Date.now()
      }));
    });

    es.addEventListener('orders', (evt: MessageEvent) => {
      try {
        const data = JSON.parse(evt.data);
        setSSEState(prev => ({
          ...prev,
          isConnected: true,
          lastHeartbeatTime: Date.now()
        }));
        dispatch({
          type: 'ORDERS_RECEIVED',
          payload: { ordersToFilter: data }
        });
      } catch (err) {
        // Keep connection alive; just record parse error
        setSSEState(prev => ({ ...prev, error: new Error('Malformed orders payload') }));
      }
    });

    es.addEventListener('heartbeat', () => {
      setSSEState(prev => ({
        ...prev,
        isConnected: true,
        lastHeartbeatTime: Date.now()
      }));
    });

    es.onerror = () => {
      // EventSource will auto-reconnect; mark as disconnected for UI
      setSSEState(prev => ({
        ...prev,
        isConnected: false,
        error: new Error('SSE connection error')
      }));
      // Do not close here; allow built-in reconnection
    };
  };

  // Heartbeat guard flips UI to disconnected if server is silent for too long
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      setSSEState(prev => {
        if (prev.lastHeartbeatTime && Date.now() - prev.lastHeartbeatTime > HEARTBEAT_TIMEOUT) {
          return { ...prev, isConnected: false };
        }
        return prev;
      });
    }, 2000);
    return () => clearInterval(id);
  }, [enabled]);

  // Connect and cleanup on deps change
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      closeIfOpen();
      setSSEState(prev => ({ ...prev, isConnected: false }));
    }
    return () => {
      closeIfOpen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, selectedCellId, landingZoneId]);

  return {
    isConnected: sseState.isConnected,
    hasError: sseState.error !== null,
    isRefreshingToken: sseState.isRefreshingToken,
    hasReachedMaxRetries: sseState.hasReachedMaxRetries,
    error: sseState.error
  };
};


